import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import confetti from "canvas-confetti";
import api from "@/lib/api";
import { CheckCircle, Circle, ArrowRight, X, Rocket, UsersThree } from "@phosphor-icons/react";

export default function OnboardingChecklist() {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { api.get("/onboarding").then((r) => setData(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (data?.all_done && localStorage.getItem("bt_onboarding_celebrated") !== "1") {
      localStorage.setItem("bt_onboarding_celebrated", "1");
      const fire = (ratio, opts) => confetti({ particleCount: Math.floor(120 * ratio), spread: 70, origin: { y: 0.35 }, colors: ["#0A0A0A", "#F59E0B", "#10B981"], ...opts });
      fire(0.5, { startVelocity: 45 });
      fire(0.3, { spread: 100, decay: 0.92, scalar: 1.1 });
      fire(0.25, { spread: 120, startVelocity: 30 });
    }
  }, [data]);

  if (!data || hidden) return null;
  if (localStorage.getItem("bt_onboarding_dismissed") === "1") return null;
  if (data.all_done && localStorage.getItem("bt_onboarding_done") === "1") return null;

  const pct = Math.round((data.completed / data.total) * 100);
  const dismiss = () => {
    localStorage.setItem("bt_onboarding_dismissed", "1");
    setHidden(true);
  };
  const dismissDone = () => {
    localStorage.setItem("bt_onboarding_done", "1");
    setHidden(true);
  };

  return (
    <div className="border-2 border-[#0A0A0A] mb-8 reveal panel" data-testid="onboarding-checklist">
      <div className="flex items-center justify-between px-5 py-4 border-b-2 border-[#0A0A0A] bg-[#0A0A0A] text-white">
        <div className="flex items-center gap-2">
          <Rocket size={18} weight="fill" className="text-[#F59E0B]" />
          <span className="font-heading font-bold">
            {data.all_done ? "You're all set up! 🎉" : "Get started with BuildTrack"}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="font-mono-fig text-sm tabular-nums">{data.completed}/{data.total}</span>
          <button onClick={data.all_done ? dismissDone : dismiss} data-testid="onboarding-dismiss" className="text-white/70 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>
      </div>

      <div className="h-2 bg-secondary">
        <div className="h-full bg-[#F59E0B] transition-all duration-500" style={{ width: `${pct}%` }} data-testid="onboarding-progress" />
      </div>

      {data.all_done ? (
        <div className="px-5 py-8 text-center" data-testid="onboarding-complete">
          <p className="font-heading text-xl font-bold mb-1">Nice work — you're fully set up!</p>
          <p className="text-sm text-muted-foreground mb-5">You've got projects, budgets and vendors in place. Bring your team along to collaborate.</p>
          <div className="flex items-center justify-center gap-2">
            <button onClick={() => navigate("/team")} data-testid="onboarding-invite-team"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0A0A0A] text-white font-bold text-sm uppercase tracking-wider hover-lift border-2 border-[#0A0A0A]">
              <UsersThree size={16} /> Invite your team
            </button>
            <button onClick={dismissDone} data-testid="onboarding-got-it" className="px-4 py-2.5 text-sm font-medium text-muted-foreground hover:text-primary">
              Got it
            </button>
          </div>
        </div>
      ) : (
        <div className="divide-y divide-border">
          {data.steps.map((s) => (
            <button
              key={s.key}
              onClick={() => !s.done && navigate(s.cta)}
              data-testid={`onboarding-step-${s.key}`}
              disabled={s.done}
              className={`w-full flex items-center gap-4 px-5 py-4 text-left transition-colors ${s.done ? "cursor-default" : "hover:bg-secondary group"}`}
            >
              {s.done
                ? <CheckCircle size={22} weight="fill" className="text-emerald-600 shrink-0" />
                : <Circle size={22} className="text-muted-foreground shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className={`font-medium ${s.done ? "text-muted-foreground line-through" : ""}`}>{s.label}</div>
                {!s.done && <div className="text-xs text-muted-foreground mt-0.5">{s.hint}</div>}
              </div>
              {!s.done && <ArrowRight size={16} className="text-muted-foreground shrink-0 group-hover:translate-x-1 transition-transform" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
